import { closeAllCodeNavCacheSqliteConnections } from '../../../../sqlite/code-nav-cache/database.js';
import { rejectAllPendingRequests, resetWorkerReadyState } from '../client-state.js';
import { clearInProcessBridge, getInProcessBridge } from '../in-process-bridge.js';
import type { SymbolIndexWorkerInMessage, SymbolIndexWorkerOutMessage } from '../protocol.js';
import { CliSqliteInterrupted } from '../../../../sqlite/sqlite-errors.js';
import {
  getTerminatePromise,
  getWorker,
  setTerminatePromise,
  setWorker,
  WORKER_SHUTDOWN_GRACE_MS,
} from './state.js';

async function shutdownWorkerThread(): Promise<void> {
  resetWorkerReadyState();
  rejectAllPendingRequests(new CliSqliteInterrupted());

  const activeWorker = getWorker();
  if (activeWorker != null) {
    setWorker(null);
    await Promise.race([
      new Promise<void>((resolve) => {
        const onMessage = (message: SymbolIndexWorkerOutMessage) => {
          if (message.type !== 'shutdownComplete') return;
          activeWorker.off('message', onMessage);
          resolve();
        };
        activeWorker.on('message', onMessage);
        try {
          activeWorker.postMessage({ type: 'shutdown' } satisfies SymbolIndexWorkerInMessage);
        } catch {
          activeWorker.off('message', onMessage);
          resolve();
        }
      }),
      new Promise<void>((resolve) => setTimeout(resolve, WORKER_SHUTDOWN_GRACE_MS)),
    ]);
    await activeWorker.terminate();
  } else {
    const inProcess = getInProcessBridge();
    if (inProcess != null) {
      await Promise.race([
        new Promise<void>((resolve) => {
          const onMessage = (message: SymbolIndexWorkerOutMessage) => {
            if (message.type !== 'shutdownComplete') return;
            inProcess.off('message', onMessage);
            resolve();
          };
          inProcess.on('message', onMessage);
          inProcess.postMessage({ type: 'shutdown' });
        }),
        new Promise<void>((resolve) => setTimeout(resolve, WORKER_SHUTDOWN_GRACE_MS)),
      ]);
    }
  }

  clearInProcessBridge();
  closeAllCodeNavCacheSqliteConnections();
}

export async function terminateSymbolIndexWorker(): Promise<void> {
  if (getTerminatePromise() != null) return getTerminatePromise()!;
  const promise = shutdownWorkerThread().catch(() => {
    closeAllCodeNavCacheSqliteConnections();
  });
  setTerminatePromise(promise);
  return promise;
}
